import React from 'react';
import { connect } from 'react-redux';
import { first, noop } from 'lodash';
import { bindActionCreators } from 'redux';

import fetchStylistsBySalon from 'actions/stylist/fetchStylistsBySalon.action';
import fetchUserEmailsBySalon from 'actions/user/fetchUserEmailsBySalon.action';
import validateName from 'bl/validation/validateName.bl';
import validateNameLength from 'bl/validation/validateNameLength.bl';
import SalonModal from 'components/dialog/SalonModal.component';
import SalonButton from 'components/form/Button.component';
import CheckboxInput from 'components/form/CheckboxInput.component';
import Icon from 'components/generic/Icon.component';
import IconStatus from 'components/generic/IconStatus.component';
import TooltipForReceptionist from 'components/TooltipForReceptionist';
import PERM from 'constants/core/perm.const';
import IN from 'constants/in';
import { NO, NOT } from 'constants/no';
import PLAN_NAME from 'constants/plan.const';
import PRICE_PER_STYLIST from 'constants/settings/ncobStaffPrices.const';
import { withTranslation } from 'providers/GILT.provider';
import { selectCurrentSalonNewPricing } from 'selectors/currentSalon.selectors';
import getAny from 'utils/accessors/getAny.util';
import getString from 'utils/accessors/getString.util';
import fromAnyToString from 'utils/converters/fromAnyToString';
import { bodAsIso, nowAsMoment } from 'utils/date/now.util';
import determineFirstNonNil from 'utils/determineFirstNonNil';
import windowScrollToPosition from 'utils/dom/windowScrollTo.util';
import logger from 'utils/logger.util';
import isFullArray from 'utils/predicates/array/isFullArray.util';
import byId from 'utils/predicates/byId.util';

import { validateEmailWithDomain } from 'lib/validators';
import { AddStylist } from 'onboarding/stylists/AddStylist';
import ListHeader from 'onboarding/stylists/ListHeader.component';
import PageHeader from 'onboarding/stylists/PageHeader.component';
import StylistField from 'onboarding/stylists/StylistField';
import EssentialOrAdvancedModal from 'onboarding/stylists/Stylists/EssentialOrAdvancedModal.component';
import OtherPlansModal from 'onboarding/stylists/Stylists/OtherPlansModal.component';
import Button from 'settings/page-elements/Button';

import CN from './StylistsScreen.module.scss';
const L = logger('StylistsScreen');

const calculateStylistAndCategoryExecutions = ({ stylist, props }) => ({
  categoryExecutions: (stylist.categories || props.categories || []).filter(({ id }) => id).reduce((executions, { id }) => [...executions, { category: id }], []),
});

const initStylist = ({ roles }) => ({
  user_name: '',
  email: '',
  phone: '',
  status: 'active',
  gender: 'female',
  role: first(roles),
});

const initStylistsMap = (props) => {
  const stylist = initStylist(props);
  const executions = calculateStylistAndCategoryExecutions({ stylist, props });
  return {
    [NO.id]: { stylist, categoryExecutions: executions.categoryExecutions },
  };
};

const mergeStylists = ({ newStylists, editedStylistsMap, oldEditedIds }) => {
  if (!newStylists) {
    return editedStylistsMap;
  }

  const keys = Object.keys(editedStylistsMap);

  for (let i = 0; i < newStylists.length; i += 1) {
    for (let j = 0; j < keys.length; j += 1) {
      const newStylist = newStylists[i];
      const editedStylistContainer = editedStylistsMap[keys[j]];
      const editedStylist = editedStylistContainer && editedStylistContainer.stylist;

      if (!editedStylist) {
        // eslint-disable-next-line no-continue
        continue;
      }

      const newStylistEmail = newStylist.email;
      const editedStylistEmail = editedStylist.email;

      if (!newStylistEmail || !editedStylistEmail || newStylistEmail !== editedStylistEmail) {
        // eslint-disable-next-line no-continue
        continue;
      }

      // FIXME: Workaround for stylist merging, for has remote login
      // SEE: https://getslick.atlassian.net/browse/GS-10315
      if (newStylist.has_remote_login !== editedStylist.has_remote_login) {
        editedStylist.has_remote_login = newStylist.has_remote_login;
      }

      // FIXME: Workaround for stylist merging, for has cal sync
      // SEE: https://getslick.atlassian.net/browse/GS-10315
      if (newStylist.has_cal_sync !== editedStylist.has_cal_sync) {
        editedStylist.has_cal_sync = newStylist.has_cal_sync;
      }

      if (oldEditedIds.indexOf(newStylist.id) === NOT.found) {
        editedStylistContainer.stylist = { ...editedStylist, ...newStylist };
      }
    }
  }

  return editedStylistsMap;
};

class StylistsScreen extends React.Component {
  static defaultProps = {
    quickSetupLayout: false,
    hideAddBtn: false,
    hideBtnWhenEditStylist: false,
    addBtnDisabled: true,
    addStylistBtn: noop,
    editStylistSaveBtn: noop,
  };

  constructor(props) {
    super(props);

    const today = bodAsIso();

    this.state = {
      editedStylistsMap: initStylistsMap(props),
      btnEnabledMap: {
        [NO.id]: {
          enabled: false,
          nameHasError: true,
          emailHasError: false,
          phoneHasError: false,
        },
      },
      serviceEditOptions: {
        update_related_custom_priced_procedures: false,
        update_related_default_priced_procedures: false,
        update_procedures_older_than: today,
      },
      confirmModalOpen: false,
      stylistChangedRole: initStylist(props),
      previouseRoles: [],
      essentialOrAdvancedModal: false,
      otherPlansModal: false,
      pricePerStylist: '',
      editStylistModalOpen: false,
      isAddingNewUser: false,
      disableSaveBtn: false,
      alertSuccess: false,
    };
  }

  componentDidMount() {
    const { salonId, fetchUserEmailsBySalon, fetchStylistsBySalon } = this.props;
    fetchUserEmailsBySalon(salonId);
    // the following is needed for the embedded StylistField component
    // FIXME: it can be embedded in StylistField if proper isFetching check is implemented to not flood BE with requests
    fetchStylistsBySalon(salonId);
    windowScrollToPosition(0, 0);
  }

  UNSAFE_componentWillReceiveProps(nextProps) {
    const oldEditedIds = this.props.editedStylistsIds;
    const newEditedIds = nextProps.editedStylistsIds;
    const newStylists = nextProps.stylists;

    const stylistsIdsToRemove = oldEditedIds.filter((id) => newEditedIds.indexOf(id) === NOT.found);
    const stylistIdsToAdd = newEditedIds.filter((id) => oldEditedIds.indexOf(id) === NOT.found);

    const editedStylistsMap = { ...this.state.editedStylistsMap };
    const btnEnabledMap = { ...this.state.btnEnabledMap };

    stylistsIdsToRemove.forEach((id) => {
      editedStylistsMap[id] = null;
    });

    stylistIdsToAdd.forEach((id) => {
      const stylist = newStylists.find((st) => st.id === id);
      if (stylist) {
        const executions = calculateStylistAndCategoryExecutions({ stylist, props: this.props });
        if (oldEditedIds.length === 0 || oldEditedIds.indexOf(stylist.id) !== NOT.found) {
          editedStylistsMap[id] = {
            stylist: { ...stylist, categories: [...stylist.categories], role: { ...stylist.role } },
            categoryExecutions: executions.categoryExecutions,
          };
        }

        btnEnabledMap[id] = {
          nameHasError: false,
          enabled: true,
          emailHasError: false,
          phoneHasError: false,
        };
      }
    });

    // reset the stylist to add if the component is about to be opened
    if (!this.props.hideAddBtn && nextProps.hideAddBtn) {
      const stylist = initStylist(nextProps);
      const executions = calculateStylistAndCategoryExecutions({ stylist, props: nextProps });
      editedStylistsMap[NO.id] = {
        stylist,
        categoryExecutions: executions.categoryExecutions,
      };
    }

    this.setState({
      editedStylistsMap: mergeStylists({ newStylists, editedStylistsMap, oldEditedIds }),
      btnEnabledMap,
    });
  }

  componentDidUpdate() {
    const otherStylists = document.getElementsByClassName('sibling-stylists');
    if (this.props.hideAddBtn) {
      for (let i = 0, len = otherStylists.length; i < len; i += 1) {
        otherStylists[i].style.display = 'none';
      }
    } else {
      for (let i = 0, len = otherStylists.length; i < len; i += 1) {
        otherStylists[i].style.display = 'block';
      }
    }
  }

  // close modal so 'add a new user' will show
  componentWillUnmount() {
    this.closeEditStylistModal();
  }

  onNameLostFocus = (value, stylistId) => {
    const editedStylistsMap = Object.assign({}, this.state.editedStylistsMap);
    if (editedStylistsMap[stylistId] && editedStylistsMap[stylistId].stylist) {
      editedStylistsMap[stylistId].stylist.user_name = value;
      editedStylistsMap[stylistId].stylist.title = value;
      this.setState({ editedStylistsMap }, () => this.validateName(value, stylistId));
    }
  };
  onPhoneLostFocus = (value, stylistId) => {
    const editedStylistsMap = Object.assign({}, this.state.editedStylistsMap);
    editedStylistsMap[stylistId].stylist.phone = value;
    this.setState({ editedStylistsMap }, () => this.checkButton(stylistId));
  };

  onRoleChange = (roleId, stylistId) => {
    const editedStylistsMap = Object.assign({}, this.state.editedStylistsMap);
    const { stylist } = editedStylistsMap[stylistId];
    let previouseRole;
    if (stylist && stylist.role) {
      previouseRole = { stylistId, roleId: stylist.role.id };
    }
    const previouseRoles = this.state.previouseRoles.slice();
    if (previouseRole) {
      previouseRoles.push(previouseRole);
    }
    this.setState({ previouseRoles });
    for (let i = 0; i < this.props.roles.length; i += 1) {
      if (roleId === this.props.roles[i].id) {
        stylist.role = this.props.roles[i];
        break;
      }
    }

    editedStylistsMap[stylistId] = {
      stylist,
      categoryExecutions: editedStylistsMap[stylistId].categoryExecutions,
    };
    this.setState({ editedStylistsMap }, () => this.checkButton(stylistId));
  };

  onPermissionChange = (permission, stylistId) => {
    const editedStylistsMap = Object.assign({}, this.state.editedStylistsMap);
    const { stylist } = editedStylistsMap[stylistId];
    stylist.permission_level = permission.toLowerCase();

    editedStylistsMap[stylistId] = {
      stylist,
      categoryExecutions: editedStylistsMap[stylistId].categoryExecutions,
    };
    this.setState({ editedStylistsMap }, () => this.checkButton(stylistId));
  };

  onExecutionPriceChange = (serviceId, price, stylistId) => {
    const editedStylistsMap = Object.assign({}, this.state.editedStylistsMap);
    this.setState({ editedStylistsMap }, () => this.checkButton(stylistId));
  };

  onCategoryCheckboxChange = (categoryId, stylistId) => {
    const editedStylistsMap = Object.assign({}, this.state.editedStylistsMap);
    const categoryExecutions = editedStylistsMap[stylistId].categoryExecutions.slice();
    for (let i = 0; i < categoryExecutions.length; i += 1) {
      if (categoryExecutions[i].category === categoryId) {
        categoryExecutions.splice(i, 1);
        editedStylistsMap[stylistId].categoryExecutions = categoryExecutions;

        this.setState({ editedStylistsMap }, () => this.checkButton(stylistId));
        return;
      }
    }
    categoryExecutions.push({
      category: categoryId,
    });

    editedStylistsMap[stylistId].categoryExecutions = categoryExecutions;
    this.setState({ editedStylistsMap }, () => this.checkButton(stylistId));
  };

  onServiceCheckboxChange = (serviceId, stylistId) => {
    const editedStylistsMap = Object.assign({}, this.state.editedStylistsMap);
    this.setState({ editedStylistsMap }, () => this.checkButton(stylistId));
  };

  onSuccessfullyAdded = () => {
    L.debug('onSuccessfullyAdded()');
    this.setState({ essentialOrAdvancedModal: false, otherPlansModal: false });
  };

  onPhoneNumberValid = (valid, value, stylistId) => {
    const btnEnabledMap = Object.assign({}, this.state.btnEnabledMap);
    if (value === '') {
      btnEnabledMap[stylistId].phoneHasError = false;
    } else {
      btnEnabledMap[stylistId].phoneHasError = !valid;
    }
    this.setState({ btnEnabledMap }, () => {
      this.checkButton(stylistId);
    });
  };

  onCheckboxClick = (valueToChange) => {
    const newServiceToEdit = this.state.serviceEditOptions;
    newServiceToEdit[valueToChange] = !newServiceToEdit[valueToChange];
    this.setState({ serviceEditOptions: newServiceToEdit });
  };

  onDefaultPriceClick = () => {
    this.onCheckboxClick('update_related_default_priced_procedures');
  };

  onCustomPriceClick = () => {
    this.onCheckboxClick('update_related_custom_priced_procedures');
  };

  onSelfEmployedChange = (selfEmployed, stylistId) => {
    const editedStylistsMap = Object.assign({}, this.state.editedStylistsMap);
    editedStylistsMap[stylistId].stylist.is_self_employed = selfEmployed;
    this.setState({ editedStylistsMap });
  };

  onWaitlistChange = (isWaitlist, stylistId) => {
    const editedStylistsMap = Object.assign({}, this.state.editedStylistsMap);
    editedStylistsMap[stylistId].stylist.serves_as_waitlist = isWaitlist;
    this.setState({ editedStylistsMap });
  };

  handleEmailLostFocus = (options) => {
    L.debug('handleEmailLostFocus()', options);

    const { email, userId, stylistId } = options || {};
    const stylist = this.findEditedStylist(stylistId);

    if (!stylist) {
      return void L.warn("Couldn't find edited stylist for stylistId:", stylistId);
    }

    // FIXME: this is changing the actual stylist object, not a clone of it, unlike editedStylistsMap
    stylist.email = email.toLowerCase();
    stylist.user_id = determineFirstNonNil(userId, stylist.user_id);

    // shallow copy to trigger re-render
    const newState = { editedStylistsMap: { ...this.state.editedStylistsMap } };
    const callback = () => this.validateEmail(email, stylistId);
    return void this.setState(newState, callback);
  };

  findEditedStylist = (stylistId) => getAny(null, ['editedStylistsMap', stylistId, 'stylist'], this.state);

  addSave = async (options) => {
    const { stylistId, permission, hasColumn, userId } = options || {};
    const { currentSalon, addStylistBtn, stylists, editStylistSaveBtn, salonId, roles } = this.props;
    const editedStylist = getAny(undefined, ['editedStylistsMap', stylistId, 'stylist'], this.state);
    const currentRoleId = getAny(undefined, 'role.id', editedStylist);

    editedStylist.has_column = hasColumn;
    editedStylist.permission_level = permission;

    L.debug('addSave()', options, { editedStylist, props: this.props, state: this.state });

    if (stylistId !== NO.id) {
      if (!editStylistSaveBtn) {
        return void L.warn('No editStylistSaveBtn:', editStylistSaveBtn);
      }

      const originalRoleId = getAny(undefined, 'role.id', first(stylists.filter(byId(editedStylist.id))));
      if (originalRoleId === currentRoleId) {
        return editStylistSaveBtn({ stylist: editedStylist, salonId, userId });
      }

      editedStylist.role = first(roles.filter(({ id }) => id === currentRoleId));
      return void this.setState({ confirmModalOpen: true, stylistChangedRole: editedStylist }, () => void editStylistSaveBtn({ stylist: editedStylist, salonId, userId }));
    }

    if (!addStylistBtn) {
      return void L.warn('No addStylistBtn:', addStylistBtn);
    }

    const planName = fromAnyToString(currentSalon && currentSalon.pricing_plan);
    const isWaitlist = editedStylist && editedStylist.serves_as_waitlist === true;

    if (planName === PLAN_NAME.essential && !isWaitlist) {
      return addStylistBtn({ stylist: editedStylist, salonId, userId });
    }

    if (planName === PLAN_NAME.advanced && !isWaitlist) {
      return addStylistBtn({ stylist: editedStylist, salonId, userId });
    }

    if (planName === PLAN_NAME.supercharged || !planName || isWaitlist) {
      return addStylistBtn({ stylist: editedStylist, salonId, userId });
    }

    return void L.warn('Unhandled planName:', planName, 'isWaitlist:', isWaitlist);
  };

  editSave = async (options) => {
    const { isNewPricing } = this.props;
    const essenPricePerSty = isNewPricing ? PRICE_PER_STYLIST.essentialNewPrice : PRICE_PER_STYLIST.essentialOldPrice;
    const advPricePerSty = isNewPricing ? PRICE_PER_STYLIST.advNewPrice : PRICE_PER_STYLIST.advOldPrice;

    L.debug('editSave()', options, { props: this.props, state: this.state });

    const { stylistId, permission, hasColumn, editedStylistId } = options || {};
    const { currentSalon } = this.props;

    const editedStylist = getAny(undefined, ['editedStylistsMap', stylistId, 'stylist'], this.state);
    editedStylist.has_column = hasColumn;
    editedStylist.permission_level = permission;

    const isWaitlist = editedStylist.serves_as_waitlist === true;
    const planName = currentSalon && currentSalon.pricing_plan;

    if (planName === PLAN_NAME.essential && !isWaitlist && hasColumn) {
      this.setState({ essentialOrAdvancedModal: true, pricePerStylist: essenPricePerSty });
    }

    if (planName === PLAN_NAME.advanced && !isWaitlist && hasColumn) {
      this.setState({ essentialOrAdvancedModal: true, pricePerStylist: advPricePerSty });
    }

    if (planName === PLAN_NAME.supercharged || !planName || isWaitlist) {
      this.setState({ otherPlansModal: true });
    }

    if (planName === PLAN_NAME.supercharged && !hasColumn) {
      this.setState({ otherPlansModal: false });
    }

    const found = this.findEditedStylist(editedStylistId) || {};
    const userId = found.user_id || options.userId;
    return this.withCloseEditStylistModal(this.addSave({ ...options, userId }));
  };

  handleEditing = (stylistId) => {
    const { editStylist, stylists } = this.props;

    const stylist = stylists.find(({ id }) => id === stylistId);
    if (stylist) {
      editStylist({ stylistId, stylist });
    }

    this.setState({ editStylistModalOpen: true }, () => {
      windowScrollToPosition(0, 0);
    });
  };

  checkButton = (stylistId) => {
    const { btnEnabledMap } = this.state;

    const userName = getString(['editedStylistsMap', stylistId, 'stylist', 'user_name'], this.state);
    const userNameIsNotEmpty = !!userName.replace(/\s/g, '');

    const buttonInfo = btnEnabledMap[stylistId];

    if (buttonInfo) {
      const { emailHasError, phoneHasError, nameHasError } = buttonInfo;
      buttonInfo.enabled = userNameIsNotEmpty && !nameHasError && !emailHasError && !phoneHasError;
    } else {
      L.warn('checkButton()', 'Missing button info for stylistId:', stylistId);
    }

    // shallow copy to trigger re-render
    const newState = { btnEnabledMap: { ...btnEnabledMap } };
    this.setState(newState);
  };

  openSaveBookingConfirmation = () => void this.setState({ confirmModalOpen: true });
  closeSaveBookingConfirmation = () => void this.setState({ confirmModalOpen: false });

  changeExistBookingPrice = () => {
    if (this.state.stylistChangedRole) {
      const previouseRoleId = this.state.previouseRoles.filter((r) => r.stylistId === this.state.stylistChangedRole.id)[0].roleId;
      let updateAll;
      if (this.state.serviceEditOptions.update_related_custom_priced_procedures && this.state.serviceEditOptions.update_related_default_priced_procedures) {
        updateAll = 'update_all';
      } else if (!this.state.serviceEditOptions.update_related_custom_priced_procedures && this.state.serviceEditOptions.update_related_default_priced_procedures) {
        updateAll = 'update_only_apps';
      } else if (this.state.serviceEditOptions.update_related_custom_priced_procedures && !this.state.serviceEditOptions.update_related_default_priced_procedures) {
        updateAll = 'update_only_custom_apps';
      } else {
        updateAll = '';
      }
      const stylistId = this.state.stylistChangedRole.id;
      const stylistRoleId = this.state.stylistChangedRole.role_id;
      if (updateAll && stylistId && stylistRoleId) {
        this.props.updateRolePrice(stylistId, stylistRoleId, this.props.salonId, previouseRoleId, updateAll);
      }
      const today = nowAsMoment().startOf('day');
      const initOption = {
        update_related_custom_priced_procedures: false,
        update_related_default_priced_procedures: false,
        update_procedures_older_than: today,
      };
      this.setState({ serviceEditOptions: initOption });
    }
  };

  validateEmail = (email, stylistId) => {
    const { btnEnabledMap } = this.state;
    const buttonInfo = btnEnabledMap[stylistId];
    const isValid = !!validateEmailWithDomain(email);

    L.debug('validateEmail()', { isValid, email, stylistId });
    if (email) {
      this.setState({ disableSaveBtn: !isValid });
    }

    if (buttonInfo) {
      // enable when there is no e-mail input
      buttonInfo.emailHasError = email === '' ? false : !isValid;
    } else {
      L.warn('validateEmail()', 'invalid stylist', buttonInfo, 'for stylistId', stylistId);
    }

    // shallow copy to trigger re-render
    const newState = { btnEnabledMap: { ...btnEnabledMap } };
    this.setState(newState, () => this.checkButton(stylistId));
    return isValid || !email ? '' : this.props.t('common.errors.validation.notValidEmail');
  };

  handleValidateNameLength = (value) => validateNameLength({ value });

  validateName = (value, stylistId) => {
    const btnEnabledMap = Object.assign({}, this.state.btnEnabledMap);
    if (validateName({ value }) && btnEnabledMap[stylistId]) {
      btnEnabledMap[stylistId].nameHasError = false;
      this.setState({ btnEnabledMap }, () => this.checkButton(stylistId));
      return '';
    }
    if (btnEnabledMap[stylistId]) {
      btnEnabledMap[stylistId].nameHasError = true;
      this.setState({ btnEnabledMap }, () => this.checkButton(stylistId));
      return this.props.t('common.errors.validation.noName');
    }
    return '';
  };

  closeEditStylistModal = () => {
    const editedStylistsMap = initStylistsMap(this.props);
    this.setState({ editStylistModalOpen: false, editedStylistsMap, isAddingNewUser: false });
    this.props.onAddMoreClick();
  };

  handleAddNewUser = () => {
    this.props.onAddMoreClick();
    this.setState({ isAddingNewUser: true });
  };

  handleAddWithColumn = async (options) => {
    const { localStylistId, permission, userId } = L.debug('handleAddWithColumn()', options);

    if (!localStylistId) {
      return void L.warn('handleAddWithColumn()', 'Missing localStylistId:', localStylistId);
    }

    return this.withCloseEditStylistModal(
      this.addSave({
        stylistId: localStylistId,
        permission,
        hasColumn: true,
        userId: (this.findEditedStylist(localStylistId) || {}).user_id || userId,
      }),
    );
  };

  handleAddWithoutColumn = async (options) => {
    const { localStylistId, localStylist, permission, hasColumn, userId } = L.debug('handleAddWithoutColumn()', options);

    this.setState({ alertSuccess: true });
    setTimeout(() => this.setState({ alertSuccess: false }), 3000);

    // eslint-disable-next-line camelcase
    if (!(localStylistId && 'has_column' in localStylist)) {
      return void L.warn('handleAddWithoutColumn()', "Can't find has_column property in localStylist:", localStylist);
    }

    return this.withCloseEditStylistModal(
      this.addSave({
        stylistId: localStylistId,
        permission,
        hasColumn: hasColumn || false,
        userId: (this.findEditedStylist(localStylistId) || {}).user_id || userId,
      }),
    );
  };

  withCloseEditStylistModal = (promise) => {
    promise.then(() => this.closeEditStylistModal());
    return promise;
  };

  handleStylistsRefetch = async () => {
    try {
      await this.props.fetchStylistsBySalon(this.props.salonId);
    } catch (error) {
      L.warn('handleStylistsRefetch()', ' Error during refetch:', error);
    }
  };

  render() {
    const { serviceEditOptions, essentialOrAdvancedModal, editedStylistsMap, pricePerStylist, isAddingNewUser, confirmModalOpen, otherPlansModal, editStylistModalOpen, alertSuccess, disableSaveBtn } =
      this.state;

    const {
      hideBtnWhenEditStylist,
      pinCodes,
      addBtnDisabled,
      futureAppointments,
      editStylist,
      isPosting,
      isSaved,
      settingsErrorStylistsIDs,
      quickSetupLayout,
      deleteStylist,
      hideAddBtn,
      categories,
      stylists,
      editedStylistsIds,
      currentSalon,
      blockAlertSuccess,
      t,
    } = this.props;

    const currentSalonPricingType = getAny(null, 'currentSalon.pricing_type', this.props);
    const pricingType = currentSalonPricingType || 'unknown';
    const roles = currentSalonPricingType !== 'service' ? this.props.roles : undefined;

    const emptyStylist = editedStylistsMap[NO.id].stylist;

    const addStylistRoleId = currentSalon && currentSalonPricingType === 'role' && emptyStylist.role ? emptyStylist.role.id : undefined;

    const firstEditedStylistsId = editedStylistsIds[0];

    const editedStylist = firstEditedStylistsId > 0 ? { ...getAny(undefined, [firstEditedStylistsId, 'stylist'], editedStylistsMap) } : emptyStylist;

    const editedStylistId = getAny(NO.id, 'id', editedStylist);

    const handleSave = (options) => this.editSave({ ...options, editedStylistId });
    const handlePermissionChange = (permission) => void (editedStylistId && this.onPermissionChange(permission.toLowerCase(), editedStylistId));
    const handlePhoneValidation = (valid, value) => void this.onPhoneNumberValid(valid, value, editedStylistId);
    const handleServiceChecked = (serviceId) => void this.onServiceCheckboxChange(serviceId, editedStylistId);
    const handleCategoryChecked = (categoryId) => void this.onCategoryCheckboxChange(categoryId, editedStylistId);
    const handleExecutionPriceChange = (serviceId, price) => void this.onExecutionPriceChange(serviceId, price, editedStylistId);
    const handleRoleChange = (roleId) => void this.onRoleChange(roleId, editedStylistId);
    const handleSelfEmployedChecked = (value) => void this.onSelfEmployedChange(value, editedStylistId);
    const handleWaitlistChecked = (value) => void this.onWaitlistChange(value, editedStylistId);
    const handleValidateEmail = (value) => this.validateEmail(value, editedStylistId);
    const handleEmailChange = (options) => void this.handleEmailLostFocus({ ...options, stylistId: editedStylistId });
    const handlePhoneChange = (value) => void this.onPhoneLostFocus(value, editedStylistId);
    const handleValidateName = (value) => this.validateName(value, editedStylistId);
    const handleNameChange = (value) => void this.onNameLostFocus(value, editedStylistId);

    const renderStylist = (iteratedStylist, index) => {
      const { id: iteratedStylistId } = iteratedStylist;
      const editedStylistsMapElement = getAny(undefined, ['editedStylistsMap', iteratedStylistId], this.state);
      const editedStylistsIdsLength = getAny(undefined, 'editedStylistsIds.length', this.props);
      const firstEditedStylistsId = getAny(undefined, 'editedStylistsIds.0', this.props);
      const localStylist =
        iteratedStylistId && editedStylistsMapElement && editedStylistsIdsLength > 0 && iteratedStylistId === firstEditedStylistsId
          ? { ...iteratedStylist, ...editedStylistsMapElement.stylist }
          : iteratedStylist;

      const { id: localStylistId, has_column: hasColumn } = localStylist;
      const userId = getAny(null, 'user_id', localStylist);
      const permission = getAny('', 'permission_level', localStylist).toLowerCase();

      const key = `${String(localStylistId)}-index-${index}`;

      const editedStylist = this.state.editedStylistsMap[localStylistId];
      const stylistName = editedStylist?.stylist?.user_name;

      if (localStylistId && !editedStylistsMapElement) {
        const error = settingsErrorStylistsIDs.find(({ id }) => id === localStylistId);
        return (
          <div data-bem="StylistsScreen__sibling-container" data-stylist-id={localStylistId} className="sibling-stylists" key={`${key}-sibling-container`}>
            <StylistField
              stylist={localStylist}
              isSaved={isSaved}
              errorMessage={error && error.message}
              onEditHandler={this.handleEditing}
              onDeleteHandler={deleteStylist}
              quickSetupLayout={quickSetupLayout}
            />
            <TooltipForReceptionist permission={PERM.canEditStaff} where={IN.staff} />
          </div>
        );
      }

      return (
        <div data-bem="StylistScreen__save-container" key={`${key}-save-container`} id="editedStylistDiv" className="obs-stylist-component SALON">
          <button
            data-bem="StylistScreen__close-button"
            className="close-btn hidden"
            onClick={(e) => {
              e.stopPropagation();
              if (localStylistId) {
                delete this.state.editedStylistsMap[localStylistId];
                editStylist({ stylistId: localStylistId, stylist: localStylist });
              }
            }}
          />
          <Button
            data-bem="StylistScreen__save-button--add"
            buttonName={isPosting ? t('common.saving') : t('common.buttons.add')}
            onClickButton={() => void this.handleAddWithColumn({ localStylistId, permission, userId })}
            className="btn-fix-width m-t-15 onboarding-button"
            disabled={addBtnDisabled}
          />
          {hideAddBtn && editStylistModalOpen && (
            <div data-bem="StylistScreen__edit-save-container" className="staff-btn-container">
              <button
                onClick={() => void this.handleAddWithoutColumn({ localStylistId, localStylist, permission, hasColumn, userId })}
                id="btn-save-existing-stylist"
                className="btn btn-violet"
                disabled={disableSaveBtn || !stylistName}
              >
                {isPosting ? t('common.saving') : t('common.buttons.saveChanges')}
              </button>

              <TooltipForReceptionist permission={PERM.canEditStaff} where={IN.btnSave} />
            </div>
          )}
        </div>
      );
    };

    const renderUpdatePriceModal = confirmModalOpen && futureAppointments.response && (futureAppointments.response.default_priced || futureAppointments.response.custome_priced);

    const isUpdatePriceDisabled = !serviceEditOptions.update_related_default_priced_procedures && !serviceEditOptions.update_related_custom_priced_procedures;

    return (
      <div data-bem="StylistsScreen" className="content relative">
        {alertSuccess && !blockAlertSuccess && <div className="alert success animated fadeInDown">{t('common.saved')}</div>}
        {!hideAddBtn && !quickSetupLayout && !hideBtnWhenEditStylist && <PageHeader onClick={this.handleAddNewUser} onPinToggleSuccess={this.handleStylistsRefetch} />}

        {hideAddBtn && (
          <div className="add-new-stylist-container ONBOARDING">
            <button className="close-btn hidden" onClick={this.closeEditStylistModal} />
            <AddStylist
              isPostingLoader={isPosting}
              categories={categories}
              hasEmailError={disableSaveBtn}
              isSaved={isSaved}
              isAddingNewUser={isAddingNewUser}
              pinCodes={pinCodes}
              pricingType={pricingType}
              roleId={addStylistRoleId}
              roles={roles}
              saveNewBtnName={isPosting ? t('common.saving') : t('common.buttons.saveChanges')}
              stylist={editedStylist}
              stylistId={editedStylistId}
              onAddNewStylistBtnClick={handleSave}
              onCategoryCheckboxChange={handleCategoryChecked}
              onEmailChange={handleEmailChange}
              onExecutionPriceChange={handleExecutionPriceChange}
              onNameChange={handleNameChange}
              onPermissionLevelChange={handlePermissionChange}
              onPhoneChange={handlePhoneChange}
              onPhoneNumberValid={handlePhoneValidation}
              onRoleChange={handleRoleChange}
              onSelfEmployedChange={handleSelfEmployedChecked}
              onServiceCheckboxChange={handleServiceChecked}
              onWaitlistChange={handleWaitlistChecked}
              validateEmail={handleValidateEmail}
              validateName={handleValidateName}
              validateNameLength={this.handleValidateNameLength}
            />
          </div>
        )}

        {isFullArray(stylists) && (
          <div>
            {stylists.some((stylist) => stylist.is_self_employed) && !hideAddBtn && <ListHeader title={t('stylistScreen.employeeTableHeader1')} />}

            {stylists.map((iteratedStylist, index) => iteratedStylist.is_self_employed && renderStylist(iteratedStylist, index))}
          </div>
        )}

        {isFullArray(stylists) && (
          <div>
            {stylists.some((stylist) => !stylist.is_self_employed) && !hideAddBtn && <ListHeader title={t('stylistScreen.employeeTableHeader2')} />}

            {stylists.map((iteratedStylist, index) => !iteratedStylist.is_self_employed && renderStylist(iteratedStylist, index))}
          </div>
        )}

        <SalonModal tag="ServicesLayoutLoader" isOpen={renderUpdatePriceModal} withFooter>
          <SalonModal.Header>
            <div className={CN.closeButtonContainer}>
              <SalonButton variant="empty" onClick={() => void this.closeSaveBookingConfirmation()}>
                <Icon variant="close" variantInvert />
              </SalonButton>
            </div>
          </SalonModal.Header>
          <SalonModal.Body>
            <IconStatus variant="info" width="lg" position="left">
              <Icon variant="calendarEmpty" selected width="l1" />
            </IconStatus>
            <div className={CN.contentModal}>
              <div className={CN.titleLeftModal}>{t('stylistScreen.updatePriceModal.title')}</div>
            </div>
            <div className={CN.textModal}>
              {t('stylistScreen.updatePriceModal.description1')}
              <p>
                <span className={CN.focused}>{t('stylistScreen.updatePriceModal.description2Part1')}</span> {t('stylistScreen.updatePriceModal.description2Part2')}
              </p>
              {futureAppointments.response && !!futureAppointments.response.default_priced && (
                <div className={CN.space}>
                  <CheckboxInput variant="tick" checked={serviceEditOptions.update_related_default_priced_procedures} onClick={this.onDefaultPriceClick}>
                    {t('stylistScreen.updatePriceModal.updateAll')} <span className={CN.textBold}> {futureAppointments?.response?.default_priced} </span>
                    {t('stylistScreen.updatePriceModal.upcomingBookings')}
                  </CheckboxInput>
                </div>
              )}
              {futureAppointments.response && !!futureAppointments.response.custom_priced && (
                <CheckboxInput variant="tick" checked={serviceEditOptions.update_related_custom_priced_procedures} onClick={this.onCustomPriceClick}>
                  {t('stylistScreen.updatePriceModal.updateAll')} <span className={CN.textBold}> {futureAppointments?.response?.custom_priced} </span>
                  {t('stylistScreen.updatePriceModal.customBookings')}
                </CheckboxInput>
              )}
            </div>
          </SalonModal.Body>
          <SalonModal.Footer>
            <SalonButton variant="outline" onClick={() => void this.closeSaveBookingConfirmation()} xSpace>
              {t('stylistScreen.updatePriceModal.continueButton')}
            </SalonButton>
            <SalonButton
              variant="primaryForce"
              xSpace
              onClick={() => {
                this.changeExistBookingPrice();
                this.closeSaveBookingConfirmation();
              }}
              disabled={isUpdatePriceDisabled}
            >
              {t('stylistScreen.updatePriceModal.confirmButton')}
            </SalonButton>
          </SalonModal.Footer>
        </SalonModal>

        <EssentialOrAdvancedModal isOpen={essentialOrAdvancedModal} currentSalon={currentSalon} pricePerStylist={pricePerStylist} onClose={this.onSuccessfullyAdded} />

        <OtherPlansModal onClose={this.onSuccessfullyAdded} isOpen={otherPlansModal} />
      </div>
    );
  }
}

const mapStateToProps = (state) => ({
  currentSalon: state.currentSalon,
  stylists: state.onboarding.stylists.stylists,
  categories: state.onboarding.services.categories,
  futureAppointments: state.onboarding.services.changePrice.futureAppointments,
  roles: state.onboarding.roles.roles,
  isFetching: state.onboarding.stylists.isFetching,
  isPosting: state.onboarding.stylists.isPosting,
  isSaved: state.onboarding.stylists.isSaved,
  pinCodes: state.salon.userPermission.pinCodes,
  isNewPricing: selectCurrentSalonNewPricing(state),
});

const mapDispatchToProps = (dispatch) => bindActionCreators({ fetchUserEmailsBySalon, fetchStylistsBySalon }, dispatch);

export default connect(mapStateToProps, mapDispatchToProps)(withTranslation()(StylistsScreen));
